import { Volume } from "../baseQuantities/volume";
import { Length } from "../baseQuantities/length";
import { Angle } from "../baseQuantities/angle";
import {
  AppData,
  CalcMode,
  Calculator,
} from "../../interface/functionalityInterface";
import cubeVolumeFigure from "../../img/calc-figure-volume-cube.svg";
import rectangularPrismVolumeFigure from "../../img/calc-figure-volume-rectangularPrism.svg";
import cylinderVolumeFigure from "../../img/calc-figure-volume-cylinder.svg";
import tubeVolumeFigure from "../../img/calc-figure-volume-tube.svg";
import coneVolumeFigure from "../../img/calc-figure-volume-cone.svg";
import sphereVolumeFigure from "../../img/calc-figure-volume-sphere.svg";
import capsuleVolumeFigure from "../../img/calc-figure-volume-capsule.svg";
import sphericalCapVolumeFigure from "../../img/calc-figure-volume-sphericalCap.svg";
import ellipsoidVolumeFigure from "../../img/calc-figure-volume-ellipsoid.svg";
import torusVolumeFigure from "../../img/calc-figure-volume-torus.svg";

const cube: CalcMode = {
  id: "001cube",
  name: "cube",
  baseQuantities: [{ ...Length, name: "side", symbol: "s" }, { ...Volume }],
  figure: cubeVolumeFigure,
  equation: "V=s\u00B3",
};
const rectangularPrism: CalcMode = {
  id: "001rectangular prism",
  name: "rectangular prism",
  baseQuantities: [
    { ...Length, name: "length", symbol: "l" },
    { ...Length, name: "width", symbol: "w" },
    { ...Length, name: "height", symbol: "h" },
    { ...Volume },
  ],
  figure: rectangularPrismVolumeFigure,
  equation: "V=l*w*h",
};

const cylinder: CalcMode = {
  id: "001cylinder",
  name: "cylinder",
  baseQuantities: [
    { ...Length, name: "radius", symbol: "r" },
    { ...Length, name: "height", symbol: "h" },
    { ...Volume },
  ],
  figure: cylinderVolumeFigure,
  equation: "V=\u03C0r\u00B2*h",
};

const tube: CalcMode = {
  id: "001tube",
  name: "tube",
  baseQuantities: [
    { ...Length, name: "outer_diameter", symbol: "Do" },
    { ...Length, name: "inner_diameter", symbol: "Di" },
    { ...Length, name: "height", symbol: "h" },
    { ...Volume },
  ],
  figure: tubeVolumeFigure,
  equation: "\u03C0h/4(D\u2080-D\u1D62)",
};

const cone: CalcMode = {
  id: "001cone",
  name: "cone",
  baseQuantities: [
    { ...Length, name: "radius", symbol: "r" },
    { ...Length, name: "height", symbol: "h" },
    { ...Volume },
  ],
  figure: coneVolumeFigure,
  equation: "V=h\u03C0r\u00B2/3",
};

const sphere: CalcMode = {
  id: "001sphere",
  name: "sphere",
  baseQuantities: [{ ...Length, name: "radius", symbol: "r" }, { ...Volume }],
  figure: sphereVolumeFigure,
  equation: "V=4\u03C0r\u00B3/3",
};

const capsule: CalcMode = {
  id: "001capsule",
  name: "capsule",
  baseQuantities: [
    {
      ...Length,
      name: "radius",
      symbol: "r",
      toolTip: "cannot calculate radius",
    },
    { ...Length, name: "height", symbol: "h" },
    { ...Volume },
  ],
  figure: capsuleVolumeFigure,
  equation: "V=4\u03C0r\u00B3/3+h\u03C0r\u00B2",
};

const sphericalCap: CalcMode = {
  id: "001sphericalCap",
  name: "spherical_cap",
  baseQuantities: [
    { ...Length, name: "sphere_radius", symbol: "r" },
    {
      ...Length,
      name: "cap_height",
      symbol: "h",
      toolTip: "cannot calculate cap height",
    },
    { ...Volume },
  ],
  figure: sphericalCapVolumeFigure,
  equation: "V=\u03C0h\u00B2/3*(3r-h)",
};

const ellipsoid: CalcMode = {
  id: "001ellipsoid",
  name: "ellipsoid",
  baseQuantities: [
    { ...Length, name: "semi-axis_a", symbol: "" },
    { ...Length, name: "semi-axis_b", symbol: "" },
    { ...Length, name: "semi-axis_c", symbol: "" },
    { ...Volume },
  ],
  figure: ellipsoidVolumeFigure,
  equation: "V=4\u03C0/3*a*b*c",
};

const torus: CalcMode = {
  id: "001torus",
  name: "torus",
  baseQuantities: [
    { ...Length, name: "major_radius", symbol: "R" },
    { ...Length, name: "minor_radius", symbol: "r" },
    { ...Angle, name: "bend_angle", symbol: "\u03B8" },
    { ...Volume },
  ],
  figure: torusVolumeFigure,
  equation: "V=(\u03C0*r\u00B2)*(2*\u03C0*R)*(\u03B8/360)",
};

export const volumeCalc: Calculator = {
  type: AppData.CALCULATOR,
  updatedAt: "2022-07-26",
  name: "volume",
  branch: "Geometry",
  difficulty: "intermediate",
  inputs: [
    cube,
    rectangularPrism,
    cylinder,
    tube,
    cone,
    sphere,
    capsule,
    sphericalCap,
    ellipsoid,
    torus,
  ],
  calculate: [
    {
      name: "cube",
      equations: [
        {
          target: "volume",
          expression: "(side)^3",
        },
        {
          target: "side",
          expression: "(volume)^(1/3)",
        },
      ],
    },
    {
      name: "rectangular prism",
      equations: [
        {
          target: "volume",
          expression: "(length) * (width) * (height)",
        },
        {
          target: "length",
          expression: "(volume) / (width) / (height)",
        },
        {
          target: "width",
          expression: "(volume) / (length) / (height)",
        },
        {
          target: "height",
          expression: "(volume) / (length) / (width)",
        },
      ],
    },
    {
      name: "cylinder",
      equations: [
        {
          target: "volume",
          expression: "pi * (radius)^2 * (height)",
        },
        {
          target: "radius",
          expression: "((volume) / (height) / pi) ^ (1/2)",
        },
        {
          target: "height",
          expression: "(volume) / ((radius)^2 * pi)",
        },
      ],
    },
    {
      name: "tube",
      equations: [
        {
          target: "volume",
          expression:
            "pi * (1/4 * (outer_diameter)^2 - 1/4 * (inner_diameter)^2) * (height)",
        },
        {
          target: "outer_diameter",
          expression:
            "root(4/pi * ((volume)/(height) + pi/4 * (inner_diameter)^2))",
        },
        {
          target: "inner_diameter",
          expression:
            "root(4/pi * (pi/4 * (outer_diameter)^2 - (volume)/(height)))",
        },
        {
          target: "height",
          expression:
            "(volume) / (pi/4 * (outer_diameter)^2 - pi/4 * (inner_diameter)^2)",
        },
      ],
    },
    {
      name: "cone",
      equations: [
        {
          target: "volume",
          expression: "(pi * (radius)^2 * (height)) / 3",
        },
        {
          target: "radius",
          expression: "root(3/pi * (volume)/(height))",
        },
        {
          target: "height",
          expression: "3/(pi*(radius)^2) * (volume)",
        },
      ],
    },
    {
      name: "volume",
      equations: [
        {
          target: "volume",
          expression: "4/3 * pi * (radius) ^ 3",
        },
        {
          target: "radius",
          expression: "((3/(4*pi)) * (volume))^(1/3)",
        },
      ],
    },
    {
      name: "capsule",
      equations: [
        {
          target: "volume",
          expression: "4*pi/3 * (radius)^3 + (height)*pi*(radius)^2",
        },
        {
          target: "height",
          expression: "((volume) - 4*pi/3 * (radius)^3) / (pi * (radius)^2)",
        },
      ],
    },
    {
      name: "spherical_cap",
      equations: [
        {
          target: "volume",
          expression:
            "pi/3 * (cap_height)^2 * (3 * (sphere_radius) - (cap_height))",
        },
        {
          target: "sphere_radius",
          expression: "((3*(volume) / (pi*(cap_height)^2)) + (cap_height))/3",
        },
      ],
    },
    {
      name: "ellipsoid",
      equations: [
        {
          target: "volume",
          expression: "4*pi/3 * (semi-axis_a) * (semi-axis_b) * (semi-axis_c)",
        },
        {
          target: "semi-axis_a",
          expression: "3 * (volume) / (4*pi) / 1/((semi-axis_b)*(semi-axis_c))",
        },
        {
          target: "semi-axis_b",
          expression: "3 * (volume) / (4*pi) / 1/((semi-axis_a)*(semi-axis_c))",
        },
        {
          target: "semi-axis_c",
          expression: "3 * (volume) / (4*pi) / 1/((semi-axis_b)*(semi-axis_a))",
        },
      ],
    },
    {
      name: "torus",
      equations: [
        {
          target: "volume",
          expression:
            "2 * pi^2 * (minor_radius)^2 * (major_radius) * (bend_angle) / 360",
        },
        {
          target: "major_radius",
          expression:
            "(volume) / (2 * pi^2 * (minor_radius)^2 * (bend_angle) / 360)",
        },
        {
          target: "minor_radius",
          expression:
            "((volume) / (2 * pi^2 * (major_radius) * (bend_angle) / 360))^(0.5)",
        },
        {
          target: "bend_angle",
          expression:
            "(volume) / (2 * pi^2 * (minor_radius)^2 * (major_radius) / 360)",
        },
      ],
    },
  ],
};
